import Section from "../components/Section";
import { motion } from "framer-motion";
import { useState } from "react";

const container = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } };

export default function AboutPrevention() {
  return (
    <>
      {/* Prevention first */}
      <Section eyebrow="Prevention" title="How can we prevent Thalassemia Major?">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid md:grid-cols-3 gap-4"
        >
          <StepCard variants={item} icon="fa-user-check" title="Carrier screening before marriage">
            Know your status early with a simple blood test.
          </StepCard>
          <StepCard variants={item} icon="fa-people-group" title="If one is a carrier → test the partner">
            Understand couple risk with both reports.
          </StepCard>
          <StepCard variants={item} icon="fa-user-doctor" title="If both carriers → counseling & planning">
            Discuss options with a genetic counselor and doctor.
          </StepCard>
        </motion.div>
      </Section>

      {/* Who & When */}
      <Section eyebrow="Who & When" title="Who should get tested, and when?">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="flex flex-wrap gap-3"
        >
          <Chip variants={item} icon="fa-user-check" text="Before marriage" />
          <Chip variants={item} icon="fa-house" text="Family history of anaemia/thalassemia" />
          <Chip variants={item} icon="fa-stethoscope" text="During pregnancy (doctor-advised)" />
        </motion.div>
      </Section>

      {/* Testing */}
      <Section eyebrow="Testing" title="What tests are typically done?">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid md:grid-cols-3 gap-6"
        >
          <IconCard variants={item} icon="fa-vial" title="CBC (Complete Blood Count)">
            Screens for anaemia and red cell indices.
          </IconCard>
          <IconCard variants={item} icon="fa-file-medical" title="Hb Electrophoresis / HPLC">
            Detects haemoglobin variants; identifies carriers.
          </IconCard>
          <IconCard variants={item} icon="fa-dna" title="DNA Testing (when needed)">
            Confirms specific mutations in select cases.
          </IconCard>
        </motion.div>

        <Callout type="info">
          Only a qualified medical professional can interpret results and advise next steps.
        </Callout>
      </Section>

      {/* Results legend */}
      <Section eyebrow="Results" title="How to read results at a glance">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid md:grid-cols-3 gap-4"
        >
          <Legend variants={item} label="Normal" tone="ok" desc="No trait detected." />
          <Legend variants={item} label="Carrier (Trait)" tone="warn" desc="Usually healthy; gene can pass to children." />
          <Legend variants={item} label="Affected" tone="danger" desc="Needs specialist care and regular follow-ups." />
        </motion.div>
      </Section>

      {/* CTA */}
      <Section eyebrow="Next Step" title="Ready to take action?">
        <div className="flex flex-wrap gap-3">
          <a href="/testing-locator" className="px-4 py-2 rounded bg-rose-600 text-white">
            <i className="fa-solid fa-location-dot mr-2" /> Find a Testing Center
          </a>
          <a href="/quiz" className="px-4 py-2 rounded border">
            Take the Myths vs Facts Quiz
          </a>
        </div>
      </Section>

      {/* Micro-FAQ */}
      <Section eyebrow="FAQ" title="Quick questions">
        <Accordion
          items={[
            { 
              q: "Why is carrier testing important before marriage?", 
              a: "If both partners are carriers, there is a 25% chance with each pregnancy of having a child with Thalassemia Major. Knowing carrier status early allows informed family planning." 
            },
            { 
              q: "Is the test painful or risky?", 
              a: "No. It is a simple blood draw done at a clinic or diagnostic lab. Risks are minimal, just like any routine blood test." 
            },
            { 
              q: "How accurate are the tests?", 
              a: "CBC and Hb Electrophoresis are highly reliable in detecting thalassemia carriers. In rare cases, DNA testing may be recommended for confirmation." 
            },
            { 
              q: "When should testing be done?", 
              a: "Ideally before marriage or planning a family. Pregnant women may also be advised to undergo screening by their doctor." 
            },
            { 
              q: "Can thalassemia be prevented completely?", 
              a: "Yes. With carrier screening, counseling, and prenatal diagnosis where necessary, new cases of Thalassemia Major can be prevented." 
            },
            { 
              q: "Where can I get tested?", 
              a: "Many hospitals, pathology labs, and NGOs run thalassemia screening programs. Use the 'Find a Testing Center' tool on this site to locate one near you." 
            }
          ]}
        />
      </Section>
    </>
  );
}

function StepCard({ icon, title, children, img, variants }) {
  return (
    <motion.div variants={variants} whileHover={{ y: -3 }} className="rounded-xl border bg-white p-5 shadow-sm">
      {img && <div className="h-24 w-full overflow-hidden rounded-lg border bg-slate-50 mb-3">
        <img src={img} alt="" className="h-full w-full object-cover" />
      </div>}
      <div className="flex items-center gap-3 mb-1">
        <i className={`fa-solid ${icon} text-rose-600`} />
        <h3 className="font-semibold text-slate-900">{title}</h3>
      </div>
      <p className="text-slate-600 text-sm">{children}</p>
    </motion.div>
  );
}
function Chip({ icon, text, variants }) {
  return (
    <motion.div variants={variants} className="flex items-center gap-2 text-sm px-3 py-2 rounded border bg-slate-50">
      <i className={`fa-solid ${icon}`} />
      <span className="text-slate-700">{text}</span>
    </motion.div>
  );
}
function IconCard({ icon, title, children, img, variants }) {
  return (
    <motion.div variants={variants} whileHover={{ y: -3 }} className="rounded-xl border p-5 bg-white shadow-sm">
      {img && <div className="h-24 w-full overflow-hidden rounded-lg border bg-slate-50 mb-3">
        <img src={img} alt="" className="h-full w-full object-cover" />
      </div>}
      <div className="flex items-center gap-3">
        <i className={`fa-solid ${icon} text-rose-600`} />
        <h3 className="font-semibold text-slate-900">{title}</h3>
      </div>
      <p className="text-slate-600 mt-2 text-sm">{children}</p>
    </motion.div>
  );
}
function Callout({ type = "info", children }) {
  const map = {
    info: "border-sky-200 bg-sky-50 text-sky-800",
    ok: "border-emerald-200 bg-emerald-50 text-emerald-800",
    warn: "border-amber-200 bg-amber-50 text-amber-800",
    danger: "border-rose-200 bg-rose-50 text-rose-800",
  };
  return <div className={`mt-6 p-4 rounded-xl border-l-4 ${map[type]}`}>{children}</div>;
}
function Legend({ label, tone = "ok", desc, img, variants }) {
  const map = {
    ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warn: "bg-amber-50 text-amber-700 border-amber-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return (
    <motion.div variants={variants} className="rounded-xl border p-5 bg-white shadow-sm">
      {img && <div className="h-20 w-full overflow-hidden rounded-lg border bg-slate-50 mb-3">
        <img src={img} alt="" className="h-full w-full object-cover" />
      </div>}
      <div className={`text-xs px-2 py-1 rounded border inline-block ${map[tone]}`}>{label}</div>
      <p className="text-slate-600 mt-2 text-sm">{desc}</p>
    </motion.div>
  );
}
function Accordion({ items }) {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (idx) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <div className="rounded-xl border bg-white">
      {items.map(({ q, a }, idx) => (
        <div key={q} className="border-b last:border-b-0">
          {/* Question */}
          <button
            onClick={() => toggle(idx)}
            className="w-full flex items-center justify-between p-3 text-left"
          >
            <span className="font-medium text-slate-800">{q}</span>
            <i
              className={`fa-solid fa-chevron-down text-slate-400 transition-transform ${
                openIndex === idx ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Answer */}
          {openIndex === idx && (
            <div className="p-3 pt-0 text-slate-600">{a}</div>
          )}
        </div>
      ))}
    </div>
  );
}
